import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Loader2, PartyPopper, Target, CalendarDays } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { getPillarById } from "@/data/pillars";
import { toast } from "sonner";
import AiActionButton from "@/components/AiActionButton";

const HERO_IMAGES = {
  home: "https://images.unsplash.com/photo-1750327324378-8aadd68f0b21?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxtaW5pbWFsaXN0JTIwY2FsbSUyMGFic3RyYWN0JTIwc2FnZSUyMGdyZWVufGVufDB8fHx8MTc4NjM0NjI2OHww&ixlib=rb-4.1.0&q=85",
  family:
    "https://images.unsplash.com/photo-1714636608872-048fc9231892?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwYWJzdHJhY3QlMjBwYWludGluZyUyMGJlaWdlfGVufDB8fHx8MTc4NjM0NjI3M3ww&ixlib=rb-4.1.0&q=85",
};

function formatDate(d) {
  return fmtDate(d) || null;
}

function AchievedGoal({ goal, color }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.35 }}
      className="relative overflow-hidden rounded-xl p-4 border"
      style={{ backgroundColor: `${color}22`, borderColor: `${color}66` }}
      data-testid={`ref-goal-achieved-${goal.id}`}
    >
      <div className="flex items-start gap-3">
        <span
          className="mt-0.5 w-8 h-8 rounded-full flex items-center justify-center shrink-0"
          style={{ backgroundColor: color }}
        >
          <PartyPopper className="w-4 h-4 text-foreground/80" />
        </span>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span
              className="text-[10px] uppercase tracking-[0.16em] font-semibold px-2 py-0.5 rounded-full"
              style={{ backgroundColor: color, color: "#2C2C2C" }}
            >
              Achieved
            </span>
            {goal.target_date && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <CalendarDays className="w-3 h-3" /> {formatDate(goal.target_date)}
              </span>
            )}
          </div>
          <p className="text-foreground/90 leading-relaxed mt-1.5 font-medium">{goal.text}</p>
        </div>
      </div>
    </motion.div>
  );
}

export default function PillarPage() {
  const { pillarId } = useParams();
  const pillar = getPillarById(pillarId);
  const [content, setContent] = useState(null);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!pillar) return;
    setLoading(true);
    Promise.all([
      api.get("/pillars/content"),
      api.get(`/pillars/${pillar.name}/goals`, { params: { only_visible: true } }),
    ])
      .then(([c, g]) => {
        setContent(c.data.content[pillar.name]);
        setGoals(g.data);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [pillar]);

  if (!pillar) {
    return (
      <div className="px-8 py-16">
        <p className="text-muted-foreground">Pillar not found.</p>
        <Link to="/" className="text-foreground underline underline-offset-4">
          Back to Dashboard
        </Link>
      </div>
    );
  }

  const hero = HERO_IMAGES[pillar.id];
  const title = content?.title || pillar.tagline;
  const definition = content?.definition || pillar.definition;
  const activeGoals = goals.filter((g) => !g.achieved);
  const achievedGoals = goals.filter((g) => g.achieved);

  return (
    <motion.div
      key={pillar.id}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="max-w-3xl px-6 md:px-12 py-10 md:py-14"
      data-testid={`pillar-page-${pillar.id}`}
    >
      <div
        className="rounded-2xl overflow-hidden mb-10 h-44 md:h-56 relative flex items-end"
        style={{ backgroundColor: pillar.color }}
      >
        {hero && (
          <img
            src={hero}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-60 mix-blend-luminosity"
          />
        )}
        <div className="relative p-6 md:p-8">
          <span className="text-[11px] uppercase tracking-[0.18em] text-foreground/70">
            {pillar.colorName}
          </span>
          <h1 className="font-serif text-4xl md:text-5xl text-foreground mt-1">{pillar.name}</h1>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          <div className="flex justify-end -mt-2 mb-6">
            <AiActionButton
              action="pillar_coach"
              params={{ pillar: pillar.name }}
              label="Coach me"
              title={`Coach me on ${pillar.name}`}
              description={`Personal, focused guidance based only on your ${pillar.name} note, goals and data.`}
              testId={`coach-btn-${pillar.id}`}
              buttonClassName="group flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium border border-input bg-white/70 hover:border-foreground/40 transition-colors duration-200"
              historyQuery={{ action: "pillar_coach", pillar: pillar.name }}
            />
          </div>
          <p
            className="font-serif text-xl md:text-2xl text-foreground/90 leading-snug mb-10"
            data-testid="ref-title"
          >
            {title}
          </p>

          <section className="mb-10">
            <h2 className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-3">
              Definition
            </h2>
            <p className="text-base leading-relaxed text-foreground/90" data-testid="ref-definition">
              {definition}
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-3">
              What "good" looks like
            </h2>
            <ul className="space-y-3">
              {pillar.whatGood.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span
                    className="mt-1 w-4 h-4 rounded-full flex items-center justify-center shrink-0"
                    style={{ backgroundColor: pillar.color }}
                  >
                    <Check className="w-2.5 h-2.5 text-foreground/70" />
                  </span>
                  <span className="text-foreground/90 leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="mb-10">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                Current goals
              </h2>
              <Link
                to={`/pillar/${pillar.id}/goals`}
                className="text-xs text-foreground/70 hover:text-foreground flex items-center gap-1"
                data-testid="ref-manage-goals"
              >
                <Target className="w-3.5 h-3.5" /> Manage goals
              </Link>
            </div>

            {activeGoals.length === 0 && achievedGoals.length === 0 && (
              <p className="text-sm text-muted-foreground italic" data-testid="ref-no-goals">
                No visible goals yet. Add some on the Goals page.
              </p>
            )}

            <div className="space-y-2 mb-4">
              {activeGoals.map((goal, i) => (
                <div
                  key={goal.id}
                  className="flex items-baseline gap-3 py-3 border-b border-border last:border-0"
                  data-testid={`ref-goal-${goal.id}`}
                >
                  <span className="font-serif text-lg text-muted-foreground/70 w-6">{i + 1}</span>
                  <span className="flex-1 text-foreground/90 leading-relaxed">{goal.text}</span>
                  {goal.target_date && (
                    <span className="text-xs text-muted-foreground shrink-0 flex items-center gap-1">
                      <CalendarDays className="w-3 h-3" /> {formatDate(goal.target_date)}
                    </span>
                  )}
                </div>
              ))}
            </div>

            {achievedGoals.length > 0 && (
              <div className="space-y-2.5">
                <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground flex items-center gap-1.5">
                  <PartyPopper className="w-3.5 h-3.5" /> Celebrating
                </p>
                {achievedGoals.map((goal) => (
                  <AchievedGoal key={goal.id} goal={goal} color={pillar.color} />
                ))}
              </div>
            )}
          </section>

          {pillar.crossLinks?.length > 0 && (
            <section>
              <h2 className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-3">
                Cross-pillar links
              </h2>
              <div className="flex flex-wrap gap-2">
                {pillar.crossLinks.map((link) => (
                  <Link
                    key={link.label}
                    to={link.to}
                    data-testid={`cross-link-${pillar.id}-${link.to.split("/").pop()}`}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-secondary text-sm text-foreground/80 hover:text-foreground hover:bg-secondary/70 transition-colors duration-200"
                  >
                    {link.label}
                    <ArrowUpRight className="w-3.5 h-3.5" />
                  </Link>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </motion.div>
  );
}
